import { Colors } from "../../config/DesignConstants";
import BaseEntity from "../../core/entity/BaseEntity";
import { createGraphics, GameGraphics } from "../../core/entity/GameSprite";
import { V } from "../../core/Vector";
import { VectorButton } from "../graphics/VectorButton";
import { TextAnchor, VectorText, VerticalAnchor } from "../text/VectorText";
import { HighScoreManager } from "../util/HighScoreManager";
import MainMenu from "./MainMenu";

const LEVEL_DISPLAY_OFFSET = 1; // Offset to convert current level to completed levels count

export default class GameOverScreen extends BaseEntity {
  pausable = false; // Should render even when game is paused

  private background: GameGraphics;
  private gameOverText: VectorText;
  private finalScoreText: VectorText;
  private levelsCompletedText: VectorText;
  private highScoreText?: VectorText;
  private mainMenuButton: VectorButton;

  constructor(
    private finalScore: number = 0,
    private finalLevel: number = 1,
  ) {
    super();

    // Create background
    this.background = createGraphics("menuBackground");

    // Create "GAME OVER" title
    this.gameOverText = new VectorText({
      text: "GAME OVER",
      scale: 6.0,
      strokeWidth: 4,
      color: Colors.RED,
      anchor: TextAnchor.CENTER,
      verticalAnchor: VerticalAnchor.CENTER,
      layer: "menu",
    });

    // Create final score text
    this.finalScoreText = new VectorText({
      text: `FINAL SCORE: ${this.finalScore.toLocaleString()}`,
      scale: 2.5,
      strokeWidth: 2,
      color: Colors.RED,
      anchor: TextAnchor.CENTER,
      verticalAnchor: VerticalAnchor.CENTER,
      layer: "menu",
    });

    // Create levels completed text
    const levelsCompleted = this.finalLevel - LEVEL_DISPLAY_OFFSET;
    this.levelsCompletedText = new VectorText({
      text: `LEVELS COMPLETED: ${levelsCompleted}`,
      scale: 2.5,
      strokeWidth: 2,
      color: Colors.RED,
      anchor: TextAnchor.CENTER,
      verticalAnchor: VerticalAnchor.CENTER,
      layer: "menu",
    });

    // Create main menu button
    this.mainMenuButton = new VectorButton({
      text: "MAIN MENU",
      style: "primary",
      size: "large",
      onClick: this.handleMainMenu,
      layer: "menu",
      color: Colors.RED,
    });
  }

  private handleMainMenu = () => {
    if (!this.game) return;

    // Store game reference before destroying anything
    const game = this.game;

    // Use setTimeout to avoid race conditions
    setTimeout(() => {
      // Destroy the game flow manager (and all game entities)
      const gameFlowManager = game.entities.getById("gameFlowManager");
      gameFlowManager?.destroy();

      // Add main menu
      game.addEntity(new MainMenu());

      // Unpause the game
      game.unpause();
    }, 0);
  };

  onAdd(): void {
    // Set sprite for positioning
    this.sprite = this.background;

    // Add all UI elements
    this.addChild(this.gameOverText);
    this.addChild(this.finalScoreText);
    this.addChild(this.levelsCompletedText);
    this.addChild(this.mainMenuButton);

    // Check for high score
    this.checkHighScore();

    // Position everything
    this.updatePositions();

    // Draw the background
    this.drawBackground();
  }

  private checkHighScore(): void {
    const highScoreManager = this.game?.entities.getById(
      "highScoreManager",
    ) as HighScoreManager;
    if (!highScoreManager) return;

    // Submit the score
    const levelsCompleted = this.finalLevel - LEVEL_DISPLAY_OFFSET;
    const entry = highScoreManager.submitScore(
      this.finalScore,
      levelsCompleted,
    );

    if (entry) {
      // It's a high score!
      const rank = highScoreManager.getScoreRank(this.finalScore);

      this.highScoreText = new VectorText({
        text: rank === 1 ? "NEW HIGH SCORE!" : `HIGH SCORE #${rank}!`,
        scale: 3.0,
        strokeWidth: 2,
        color: Colors.YELLOW,
        anchor: TextAnchor.CENTER,
        verticalAnchor: VerticalAnchor.CENTER,
        layer: "menu",
      });

      this.addChild(this.highScoreText);
    }
  }

  private drawBackground(): void {
    if (!this.game) return;

    // Get screen dimensions
    const screenWidth = this.game.renderer.app.renderer.width;
    const screenHeight = this.game.renderer.app.renderer.height;

    // Create a centered background box
    const boxWidth = 500;
    const boxHeight = 400;

    this.background.clear();

    // Draw black background with some transparency
    this.background.rect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight);
    this.background.fill({
      color: 0x000000, // Pure black
      alpha: 0.9,
    });

    // Draw border
    this.background.rect(-boxWidth / 2, -boxHeight / 2, boxWidth, boxHeight);
    this.background.stroke({
      color: Colors.RED,
      width: 3,
    });

    // Position background at center of screen
    this.background.position.set(screenWidth / 2, screenHeight / 2);
  }

  private updatePositions(): void {
    if (!this.game) return;

    // Get screen dimensions
    const screenWidth = this.game.renderer.app.renderer.width;
    const screenHeight = this.game.renderer.app.renderer.height;
    const centerX = screenWidth / 2;
    const centerY = screenHeight / 2;

    // Position elements vertically
    if (this.highScoreText) {
      // Adjust layout when high score is shown
      this.gameOverText.position = V(centerX, centerY - 140);
      this.highScoreText.position = V(centerX, centerY - 80);
      this.finalScoreText.position = V(centerX, centerY - 20);
      this.levelsCompletedText.position = V(centerX, centerY + 20);
      this.mainMenuButton.setPosition(V(centerX, centerY + 90));
    } else {
      // Normal layout
      this.gameOverText.position = V(centerX, centerY - 120);
      this.finalScoreText.position = V(centerX, centerY - 40);
      this.levelsCompletedText.position = V(centerX, centerY);
      this.mainMenuButton.setPosition(V(centerX, centerY + 80));
    }
  }
}
